/**
 * TemplateRail — the detail page's right rail: provenance and the workflows that use the template (entity-management
 * §1.4). Demo page: /profile (side cards).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Workflow } from 'lucide-react';
import { Badge, Card, ItemList } from '../../../components/ui/index.js';
import type { AgentTemplate } from '../../../generated/api/types.gen.js';
import { sentenceCase } from '../../../components/ui/FilterSelect.js';
import { FactList, FactRow } from './detail-parts.js';

export const TemplateRail: React.FC<{ template: AgentTemplate }> = ({ template }) => {
  const workflows = template.workflows ?? [];
  return (
    <>
      <Card title="Provenance">
        <FactList>
          <FactRow label="Department" value={sentenceCase(template.departmentId ?? 'unassigned')} />
          <FactRow label="Permission tier" value={sentenceCase(template.permissionTier ?? 'observer')} />
          <FactRow label="Platform" value={template.platform ? sentenceCase(template.platform) : 'Any'} />
          <FactRow label="Category" value={template.category ? sentenceCase(template.category) : 'General'} />
          <FactRow label="Source file" value={template.sourceFilePath ?? 'Not linked'} />
        </FactList>
      </Card>
      <Card title={`Used by workflows (${workflows.length})`}>
        {workflows.length === 0 ? (
          <p className="text-sm text-muted">No workflow uses this template yet.</p>
        ) : (
          <ItemList
            aria-label="Workflows that use this template"
            items={workflows.map((workflow) => ({
              id: String(workflow.id),
              leading: <Workflow size={16} className="text-muted" aria-hidden="true" />,
              title: (
                <Link to={`/agents/workflows/${workflow.id}`} className="hover:text-accent-text">
                  {workflow.name}
                </Link>
              ),
              value: (
                <Badge variant={workflow.status === 'active' ? 'success' : 'neutral'}>
                  {sentenceCase(workflow.status)}
                </Badge>
              ),
            }))}
          />
        )}
      </Card>
    </>
  );
};
